import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { memberAPI, relationshipAPI } from '../services/api';
import { useData } from '../contexts/DataContext';
import './AddRelativeModal.css';

const RELATIONSHIP_TYPE_KEYS = [
  { value: 'parent', labelKey: 'relationships.parent' },
  { value: 'child', labelKey: 'relationships.child' },
  { value: 'sibling', labelKey: 'relationships.sibling' },
  { value: 'spouse', labelKey: 'relationships.spouse' },
  { value: 'co-parent', labelKey: 'relationships.coParent' },
];

function AddRelativeModal({ member, onClose, onSave }) {
  const { t } = useTranslation();
  const { members: cachedMembers, relationships, refreshMembers, refreshRelationships } = useData();

  // Memoize translated relationship types
  const RELATIONSHIP_TYPES = useMemo(() =>
    RELATIONSHIP_TYPE_KEYS.map(type => ({
      value: type.value,
      label: t(type.labelKey)
    })),
    [t]
  );
  const [mode, setMode] = useState('existing'); // 'existing' or 'new'
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    relationshipType: '',
  });
  const [relationshipInput, setRelationshipInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedRelationshipIndex, setHighlightedRelationshipIndex] = useState(-1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Existing person selection state
  const [selectedExistingMember, setSelectedExistingMember] = useState(null);
  const [personSearchInput, setPersonSearchInput] = useState('');
  const [showPersonSuggestions, setShowPersonSuggestions] = useState(false);
  const [highlightedPersonIndex, setHighlightedPersonIndex] = useState(-1);

  // Get IDs of people who already have a relationship with the current member
  const existingRelativeIds = useMemo(() => {
    const ids = new Set();
    relationships.forEach(rel => {
      if (rel.person1_id === member.id) {
        ids.add(rel.person2_id);
      } else if (rel.person2_id === member.id) {
        ids.add(rel.person1_id);
      }
    });
    return ids;
  }, [relationships, member.id]);

  // Use cached members, filtered to exclude current member and existing relatives
  const allMembers = useMemo(() =>
    cachedMembers.filter(m => m.id !== member.id && !existingRelativeIds.has(m.id)),
    [cachedMembers, member.id, existingRelativeIds]
  );

  // Filter person suggestions based on search input
  const filteredPersons = useMemo(() => {
    if (!personSearchInput) return allMembers;
    const lower = personSearchInput.toLowerCase();
    return allMembers.filter(
      (m) =>
        m.first_name.toLowerCase().includes(lower) ||
        m.last_name?.toLowerCase().includes(lower) ||
        `${m.first_name} ${m.last_name || ''}`.toLowerCase().includes(lower)
    );
  }, [personSearchInput, allMembers]);

  // Filter relationship suggestions based on input
  const filteredSuggestions = useMemo(() => {
    if (!relationshipInput) return RELATIONSHIP_TYPES;
    const lower = relationshipInput.toLowerCase();
    return RELATIONSHIP_TYPES.filter(
      (type) =>
        type.label.toLowerCase().includes(lower) ||
        type.value.toLowerCase().includes(lower)
    );
  }, [relationshipInput]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleRelationshipInputChange = useCallback((e) => {
    const value = e.target.value;
    setRelationshipInput(value);
    setShowSuggestions(true);
    setHighlightedRelationshipIndex(-1);

    // Check if input matches a relationship type exactly
    const match = RELATIONSHIP_TYPES.find(
      (type) => type.label.toLowerCase() === value.toLowerCase()
    );
    if (match) {
      setFormData((prev) => ({ ...prev, relationshipType: match.value }));
    } else {
      setFormData((prev) => ({ ...prev, relationshipType: '' }));
    }
  }, []);

  const handleSuggestionSelect = useCallback((type) => {
    setRelationshipInput(type.label);
    setFormData((prev) => ({ ...prev, relationshipType: type.value }));
    setShowSuggestions(false);
    setHighlightedRelationshipIndex(-1);
  }, []);

  const handleRelationshipFocus = useCallback(() => {
    setShowSuggestions(true);
  }, []);

  const handleRelationshipBlur = useCallback(() => {
    // Delay to allow click on suggestion
    setTimeout(() => setShowSuggestions(false), 200);
  }, []);

  const handleRelationshipKeyDown = useCallback((e) => {
    if (!showSuggestions || filteredSuggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedRelationshipIndex((prev) =>
          prev < filteredSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedRelationshipIndex((prev) =>
          prev > 0 ? prev - 1 : filteredSuggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedRelationshipIndex >= 0) {
          handleSuggestionSelect(filteredSuggestions[highlightedRelationshipIndex]);
        } else if (filteredSuggestions.length === 1) {
          handleSuggestionSelect(filteredSuggestions[0]);
        }
        break;
      case 'Tab':
        if (highlightedRelationshipIndex >= 0) {
          handleSuggestionSelect(filteredSuggestions[highlightedRelationshipIndex]);
        } else if (filteredSuggestions.length === 1) {
          e.preventDefault();
          handleSuggestionSelect(filteredSuggestions[0]);
        }
        setShowSuggestions(false);
        break;
      case 'Escape':
        setShowSuggestions(false);
        setHighlightedRelationshipIndex(-1);
        break;
      default:
        break;
    }
  }, [showSuggestions, filteredSuggestions, highlightedRelationshipIndex, handleSuggestionSelect]);

  // Person search handlers
  const handlePersonSearchChange = useCallback((e) => {
    const value = e.target.value;
    setPersonSearchInput(value);
    setShowPersonSuggestions(true);
    setSelectedExistingMember(null);
    setHighlightedPersonIndex(-1);
  }, []);

  const handlePersonSelect = useCallback((person) => {
    setSelectedExistingMember(person);
    setPersonSearchInput(`${person.first_name} ${person.last_name || ''}`.trim());
    setShowPersonSuggestions(false);
    setHighlightedPersonIndex(-1);
  }, []);

  const handlePersonSearchFocus = useCallback(() => {
    setShowPersonSuggestions(true);
  }, []);

  const handlePersonSearchBlur = useCallback(() => {
    // Delay to allow click on suggestion
    setTimeout(() => setShowPersonSuggestions(false), 200);
  }, []);

  const handlePersonSearchKeyDown = useCallback((e) => {
    if (!showPersonSuggestions || filteredPersons.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedPersonIndex((prev) =>
          prev < filteredPersons.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedPersonIndex((prev) =>
          prev > 0 ? prev - 1 : filteredPersons.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedPersonIndex >= 0) {
          handlePersonSelect(filteredPersons[highlightedPersonIndex]);
        } else if (filteredPersons.length === 1) {
          handlePersonSelect(filteredPersons[0]);
        }
        break;
      case 'Tab':
        if (highlightedPersonIndex >= 0) {
          handlePersonSelect(filteredPersons[highlightedPersonIndex]);
        } else if (filteredPersons.length === 1) {
          e.preventDefault();
          handlePersonSelect(filteredPersons[0]);
        }
        setShowPersonSuggestions(false);
        break;
      case 'Escape':
        setShowPersonSuggestions(false);
        setHighlightedPersonIndex(-1);
        break;
      default:
        break;
    }
  }, [showPersonSuggestions, filteredPersons, highlightedPersonIndex, handlePersonSelect]);

  const handleModeChange = useCallback((newMode) => {
    setMode(newMode);
    setError('');
    // Reset selections when switching modes
    if (newMode === 'existing') {
      setFormData(prev => ({ ...prev, firstName: '', lastName: '' }));
      // Show suggestions when switching to existing mode
      setShowPersonSuggestions(true);
    } else {
      setSelectedExistingMember(null);
      setPersonSearchInput('');
      setShowPersonSuggestions(false);
    }
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setError('');

      if (!formData.relationshipType) {
        setError(t('addRelative.selectRelationshipType'));
        return;
      }

      // Validate based on mode
      if (mode === 'existing') {
        if (!selectedExistingMember) {
          setError(t('addRelative.selectExistingPerson'));
          return;
        }
      } else {
        if (!formData.firstName.trim()) {
          setError(t('addRelative.firstNameRequired'));
          return;
        }
      }

      setSaving(true);

      try {
        let targetMemberId;

        if (mode === 'existing') {
          // Use the selected existing member
          targetMemberId = selectedExistingMember.id;
        } else {
          // Create the new family member
          const memberFormData = new FormData();
          memberFormData.append('first_name', formData.firstName.trim());
          memberFormData.append('last_name', formData.lastName.trim());

          const memberResponse = await memberAPI.create(memberFormData);
          targetMemberId = memberResponse.data.id;
        }

        // Determine relationship direction based on type
        // For "parent": the target person is the parent of the selected member
        // For "child": the target person is the child of the selected member
        // For others: person1 is the selected member, person2 is the target member
        let person1Id, person2Id, relType;

        if (formData.relationshipType === 'parent') {
          // Target person is parent of selected member
          person1Id = targetMemberId;
          person2Id = member.id;
          relType = 'parent';
        } else if (formData.relationshipType === 'child') {
          // Target person is child of selected member (selected is parent)
          person1Id = member.id;
          person2Id = targetMemberId;
          relType = 'parent';
        } else {
          // For sibling, spouse, co-parent - bidirectional
          person1Id = member.id;
          person2Id = targetMemberId;
          relType = formData.relationshipType;
        }

        // Create the relationship
        await relationshipAPI.create({
          person1_id: person1Id,
          person2_id: person2Id,
          relationship_type: relType,
        });

        // Refresh caches
        if (mode === 'new') {
          await refreshMembers();
        }
        await refreshRelationships();

        onSave();
      } catch (err) {
        setError(err.response?.data?.error || t('addRelative.addFailed'));
      } finally {
        setSaving(false);
      }
    },
    [formData, member.id, onSave, mode, selectedExistingMember, refreshMembers, refreshRelationships, t]
  );

  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-content">
        <div className="modal-header">
          <h2>{t('addRelative.title')}</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <p className="modal-subtitle">
          {t('addRelative.subtitle')} <strong>{member.first_name} {member.last_name}</strong>
        </p>

        <form onSubmit={handleSubmit}>
          {error && <div className="modal-error">{error}</div>}

          <div className="mode-toggle">
            <button
              type="button"
              className={`mode-btn ${mode === 'existing' ? 'active' : ''}`}
              onClick={() => handleModeChange('existing')}
            >
              {t('addRelative.existingPerson')}
            </button>
            <button
              type="button"
              className={`mode-btn ${mode === 'new' ? 'active' : ''}`}
              onClick={() => handleModeChange('new')}
            >
              {t('addRelative.newPerson')}
            </button>
          </div>

          <div className="form-group">
            <label htmlFor="relationshipType">{t('addRelative.relationship')}</label>
            <div className="autocomplete-container">
              <input
                type="text"
                id="relationshipType"
                value={relationshipInput}
                onChange={handleRelationshipInputChange}
                onFocus={handleRelationshipFocus}
                onBlur={handleRelationshipBlur}
                onKeyDown={handleRelationshipKeyDown}
                placeholder={t('addRelative.typeToSearch')}
                autoComplete="off"
              />
              {showSuggestions && filteredSuggestions.length > 0 && (
                <ul className="autocomplete-suggestions">
                  {filteredSuggestions.map((type, index) => (
                    <li
                      key={type.value}
                      onMouseDown={() => handleSuggestionSelect(type)}
                      className={`${
                        formData.relationshipType === type.value ? 'selected' : ''
                      }${highlightedRelationshipIndex === index ? ' highlighted' : ''}`}
                    >
                      {type.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {mode === 'existing' ? (
            <div className="form-group">
              <label htmlFor="personSearch">{t('addRelative.selectPerson')}</label>
              <div className="autocomplete-container">
                <input
                  type="text"
                  id="personSearch"
                  value={personSearchInput}
                  onChange={handlePersonSearchChange}
                  onFocus={handlePersonSearchFocus}
                  onBlur={handlePersonSearchBlur}
                  onKeyDown={handlePersonSearchKeyDown}
                  placeholder={t('addRelative.typeToSearch')}
                  autoComplete="off"
                />
                {showPersonSuggestions && filteredPersons.length > 0 && (
                  <ul className="autocomplete-suggestions">
                    {filteredPersons.map((person, index) => (
                      <li
                        key={person.id}
                        onMouseDown={() => handlePersonSelect(person)}
                        className={`${
                          selectedExistingMember?.id === person.id ? 'selected' : ''
                        }${highlightedPersonIndex === index ? ' highlighted' : ''}`}
                      >
                        {person.first_name} {person.last_name || ''}
                      </li>
                    ))}
                  </ul>
                )}
                {showPersonSuggestions && filteredPersons.length === 0 && (
                  <ul className="autocomplete-suggestions">
                    <li className="no-results">{t('addRelative.noMatchingPersons')}</li>
                  </ul>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="form-group">
                <label htmlFor="firstName">{t('member.firstName')}</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder={t('addRelative.enterFirstName')}
                />
              </div>

              <div className="form-group">
                <label htmlFor="lastName">{t('member.lastName')}</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder={t('addRelative.enterLastName')}
                />
              </div>
            </>
          )}

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddRelativeModal;
